import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

// Role is the source of truth in `users/{uid}.role` (FleetFlow doc-role model).
// `tenantId` is ALSO mirrored into a custom claim so rules can isolate cheaply;
// the client trusts the Firestore profile for UI scope.
export const ROLES = ['hqadmin', 'owner', 'manager', 'agent'];

// FleetFlow getUserRole() pattern: validate the stored role, never invent one.
export function getUserRole(profile) {
  const role = profile?.role;
  return ROLES.includes(role) ? role : null;
}

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // The tenant the UI is currently scoped to. Defaults to the user's home
  // tenant; HQ/Owner can switch it via the topbar. Threaded into every data
  // listener as `tenantId` (below) so views re-scope with no per-view changes.
  const [activeTenantId, setActiveTenantId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setAuthUser(null);
        setProfile(null);
        setActiveTenantId(null);
        setLoading(false);
        return;
      }
      // Re-auth in progress (e.g. the demo-role switch). Drop the PREVIOUS
      // user's profile immediately and show the loading gate, so no data
      // listener fires with a stale role/tenant against the new uid — that
      // mismatch caused transient permission-denied "list" errors.
      setLoading(true);
      setAuthUser(fbUser);
      setProfile(null);
      setActiveTenantId(null);
      try {
        const snap = await getDoc(doc(db, 'users', fbUser.uid));
        const next = snap.exists() ? { uid: fbUser.uid, ...snap.data() } : null;
        setProfile(next);
        // Reset the active tenant to the (new) user's home tenant on every
        // auth/profile change — e.g. the demo-role switch re-signs-in.
        setActiveTenantId(next?.tenantId ?? null);
      } catch {
        setProfile(null);
        setActiveTenantId(null);
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const value = useMemo(() => {
    const role = getUserRole(profile);
    const homeTenantId = profile?.tenantId ?? null;
    return {
      authUser,
      profile,
      role,
      // `tenantId` IS the active (possibly switched) tenant so listeners scope to it.
      tenantId: activeTenantId ?? homeTenantId,
      homeTenantId,
      activeTenantId: activeTenantId ?? homeTenantId,
      setActiveTenant: setActiveTenantId,
      tenantIds: profile?.tenantIds ?? [],
      storeIds: profile?.storeIds ?? [],
      territoryIds: profile?.territoryIds ?? [],
      reportsTo: profile?.reportsTo ?? null,
      active: profile?.active ?? false,
      loading,
      isAuthenticated: Boolean(authUser),
      signIn: (email, password) =>
        signInWithEmailAndPassword(auth, email, password),
      signOutUser: () => signOut(auth),
    };
  }, [authUser, profile, loading, activeTenantId]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Throwing on a missing provider enforces the lesson
// `context-hook-needs-provider-at-every-render-site` mechanically.
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error(
      'useAuth must be used within an AuthProvider — wrap the render tree (ref: context-hook-needs-provider-at-every-render-site).',
    );
  }
  return ctx;
}
